import { useState } from 'react';
import { createPortal } from 'react-dom';

import { getAttrObj, getFullDoc } from './utils';

export const defaultTestIdFull = 'secure-iframe-full';

type Props = {
  children: string;
  className?: string; // allows styling of iframe via styled-components
  testId?: string;
};

/* Secure IframeFull
 * Works well with the side-by-side editor
 * - rendering content with a portal does not trigger subsequent onLoad events when the content
 *   changes. This means that the iframe does not reset and scroll to the top while changes are made
 *   and does not create a flicker (seen in images) each time the contents are re-rendered
 *
 * This component simplifies the wrapping of a potentially dangerous html document (containing html
 * head, and body tags) with an iframe.
 *
 * This component is suitable for passing a child as stringified html. Emample:
 *   <IframeFull>
 *     {removeScriptFullDoc(content.html, cspTag)}
 *   </IframeFull>
 *
 * Note: In the interest of being thorough, it is recommended that you first sanitize the HTML
 *       content with the removeScriptFullDoc() utility from 'utils/script'. This utility also takes
 *       second (optional) parameter for a CSP that is internal to the iframe. A preconfigured CSP
 *       is available as cspTag from the constants folder in this module
 *
 * Security considerations:
 * - the iframe itself is sandboxed ('allow-same-origin' is necessary for React to manipulate the
 *   contents in real time)
 * - a CSP (if provided) is injected into the header of the iframe's content, allowing only the
 *   resources that are absolutely necessary for our members content to render correctly
 * - the html document used for the source of the iframe is sanitized with removeScriptFullDoc()
 *   prior to rendering
 *
 * Styling:
 * This component may be styled normally with styled-components
 */

export const IframeFull = ({ children, className, testId = defaultTestIdFull }: Props) => {
  const [previewHtmlNode, setPreviewHtmlNode] = useState<HTMLElement>();
  // It does not seem possible to set the documentElement directly to the full html document, but it
  // is possible to get the entire rendered doc and set the head and body
  const fullDoc = getFullDoc(children);
  const bodyAttrs = getAttrObj(fullDoc.body.attributes);

  /* Important: Firefox resets the iframe content after it receives the load event, so setting the
   * content on ComponentDidMount (useEffect with empty dependency array) will be overwritten
   * resulting in an empty iframe. To fix this behavior, set the nodes and portals following the
   * onLoad callback rather than ComponentDidMount. Also, it is important that the node references
   * are set with state in order to trigger a re-render to activate the portals (setting them via
   * useRef will not trigger a rerender which will result in an empty iframe until a rerender is
   * triggered). Finally, to avoid potential issues in Chrome, render the portals outside of the
   * iframe (they may, in some environments, get removed if rendered inside of the iframe tags) */

  const handleLoad = (e: any) => {
    // manually set reference following render/load
    // note: changing the contents via the portal(s) does not trigger subsequent onLoad events
    const docElement = e.target.contentDocument?.documentElement as HTMLElement; // the <html> node
    // remove all children (clear out default (empty) <head> & <body>) leaving only the empty <html></html>
    Array.from(docElement.children).forEach((child) => {
      child?.parentNode?.removeChild(child);
    });
    setPreviewHtmlNode(e.target.contentDocument?.documentElement);
  };

  return (
    <>
      <iframe
        className={className}
        data-test={testId}
        onLoad={handleLoad}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" // allow-same-origin: allows us to access the iframe's innards via JS; allow-popups: Allows new windows/tabs to be opened from links; allow-popups-to-escape-sandbox: allows a sandboxed document to open new windows without forcing the sandboxing flags upon them
        srcDoc={`<!DOCTYPE html>`} // give Safari a starting point, otherwise it will render and empty iframe
      />
      {previewHtmlNode &&
        createPortal(
          <>
            <head dangerouslySetInnerHTML={{ __html: fullDoc.head.innerHTML }} />
            <body {...bodyAttrs} dangerouslySetInnerHTML={{ __html: fullDoc.body.innerHTML }} />
          </>,
          previewHtmlNode,
        )}
    </>
  );
};

export default IframeFull;
